import { FileCheck2, ClipboardCheck, ShieldCheck } from "lucide-react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton, Card } from "../components/ui";
import { Seo } from "../lib/head";
import { FaqSection, CtaBand, faqPageJsonLd, type Faq } from "../components/marketing";

const FTAGS: { tag: string; name: string }[] = [
  { tag: "F756", name: "Drug Regimen Review" },
  { tag: "F757", name: "Unnecessary Drugs" },
  { tag: "F605", name: "Psychotropic Drugs / PRN limits / Freedom from Chemical Restraints" },
  { tag: "F881", name: "Antibiotic Stewardship" },
];

const FAQS: Faq[] = [
  {
    q: "What do surveyors actually cite?",
    a: "Surveyors rarely cite the clinical decision itself — they cite the absence of documentation showing it was made, communicated, and acted upon. Survey readiness is about a defensible, dated trail for every irregularity, GDR attempt, PRN limit, stop date, and indication.",
  },
  {
    q: "How does the platform make documentation defensible?",
    a: "The rules engine flags F756/F757/F605/F881 triggers as you review, and the e-signed report bundle — the monthly irregularity report, prescriber/DON signatures, and provider-response loop — becomes one survey-ready record with date stamps.",
  },
  {
    q: "Does GDR documentation come standard?",
    a: "Yes. Gradual dose reduction attempts (or a documented clinical contraindication), target symptoms, and non-pharmacologic interventions are captured in the review and carried into the signed record.",
  },
  {
    q: "Do you need our PHI to show us this?",
    a: "No. Every demo uses synthetic data only, never real resident PHI. WeConsult Rx is built for a HIPAA-conscious workflow with a BAA available.",
  },
];

export function SurveyReadiness() {
  return (
    <>
      <Seo
        title="Pharmacy Survey Readiness & GDR Documentation Software"
        description="Automate survey-ready F-tag and GDR documentation. WeConsult Rx flags F756/F757/F605/F881 as you review and captures a dated, e-signed record for your next SNF survey."
        path="/survey-readiness"
        jsonLd={faqPageJsonLd(FAQS)}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Survey readiness</Eyebrow>
            <Heading level={1} className="mt-3">
              Survey-ready F-tag &amp; GDR documentation, automated.
            </Heading>
            <Lede className="mt-6">
              Walk into survey with the psychotropic, GDR, and unnecessary-drug documentation already done —
              flagged as you review and captured as a dated, e-signed record.
            </Lede>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/resources">Get the F-tag checklist</LinkButton>
              <LinkButton to="/demo" variant="secondary">
                Book a demo
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>Coverage for the F-tags surveyors cite</Heading>
          <Lede className="mt-4">Aligned to the CMS State Operations Manual, Appendix PP, and ASCP/Beers/STOPP guidance.</Lede>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FTAGS.map((f) => (
              <Card key={f.tag} className="flex items-center gap-4">
                <span className="inline-flex items-center rounded-lg bg-forest-700 px-3 py-1.5 font-mono text-sm font-bold text-white">
                  {f.tag}
                </span>
                <span className="text-[0.9375rem] font-semibold text-ink-900">{f.name}</span>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: <ClipboardCheck className="h-6 w-6" aria-hidden />, t: "Flagged as you review", b: "Missing indications, expired stop dates, over-14-day PRN psychotropics, and overdue GDR attempts surface automatically." },
              { icon: <FileCheck2 className="h-6 w-6" aria-hidden />, t: "Captured as a signed record", b: "The monthly irregularity report, prescriber/DON e-signatures, and provider responses become one dated, survey-ready bundle." },
              { icon: <ShieldCheck className="h-6 w-6" aria-hidden />, t: "No PHI in demos", b: "See the whole workflow on synthetic data — we never ask for real resident PHI to prove it out." },
            ].map((x) => (
              <Card as="article" key={x.t} className="h-full">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-ink-50 text-ink-700">
                  {x.icon}
                </span>
                <Heading level={3} className="mt-5">
                  {x.t}
                </Heading>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-ink-600">{x.b}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <FaqSection items={FAQS} />

      <CtaBand
        title="Be survey-ready before anyone walks in"
        body="Download the free F-tag readiness checklist, or book a demo to see the documentation build itself as you review."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/resources", label: "Get the checklist" }}
      />
    </>
  );
}
